/**
 * ==========================================================================
 *  THE MINIGAME AT THE MUURIKKA, AND THE MAN IN THE DOORWAY
 * ==========================================================================
 *  Kimmo does not get into the juhlanavetta hungry. Mäysä from the ruokatiimi
 *  is standing across the porstua with his arm out and his other hand pointing
 *  down the facade at the pan, and the box that is him stays solid until there
 *  are three makkaraa on the plate.
 *
 *  Getting them is a three-card game. Stand at the pan and the cook starts
 *  working it: a second of the lasta going, then three boxes for 0.75 seconds,
 *  shortening by 0.1 seconds per helping - one meat and two vegetables in
 *  the others, all of it rolled fresh every round. `1`, `2` and `3` are the
 *  boxes. Take the meat and it goes on the plate; take a vegetable and Kimmo
 *  brings it straight back up, which costs him `AAMUPALA.vomitDuration`; take
 *  nothing and the cook sweeps it together and deals again.
 *
 *  ------------------------------------------------------------------------
 *   EVERYTHING HERE IS A PURE FUNCTION OF A STEP NUMBER
 *  ------------------------------------------------------------------------
 *  There is no clock in this file and no `Math.random()`. A round's hand comes
 *  out of `rollBoxes(startStep, round)`, and `startStep` is the simulation step
 *  Kimmo first came within `AAMUPALA.reach` of the plate - so the deal is
 *  unguessable to the player and exactly reproducible on the server. That is
 *  the same trade the beer makes, and `src/config/aamupala.ts` is where it is
 *  argued.
 *
 *  Pure of the *round number* as well as of the step, which is what the "never
 *  the same meat twice running" rule costs: a round has to know what the round
 *  before it dealt, and the only honest way to know that without keeping state
 *  is to walk the rounds from the start of the visit. See `meatIndex`.
 *
 *  ------------------------------------------------------------------------
 *   NO THREE.JS
 *  ------------------------------------------------------------------------
 *  Same split as `Door` and `Kamiina`: this owns everything the simulation can
 *  observe - the phase, the hand, the plate and Mäysä's collider - and the pan
 *  full of food, the man himself and the overlay are things that read `view()`
 *  back out. It is stepped inside the fixed timestep, so a run re-simulates to
 *  the same millisecond in a Lambda with no scene graph anywhere near it.
 * ==========================================================================
 */

import { AAMUPALA, MEATS, VEGES, isMeat, type Food } from '../config/aamupala';
import { SIMULATION_HZ } from '../config/gameplay';
import { mulberry32 } from '../utils/prng';
import type { Vec3 } from '../utils/math';
import type { Collider } from './CollisionWorld';
import type { AamupalaSpec } from './Level';

/**
 * How many boxes are dealt: one meat and the rest vegetables, so there is
 * always exactly one right answer.
 *
 * Three, and it is not a tuning value - `1`, `2` and `3` are what the input
 * format has bits for (`BUTTON.pick1`..`pick3` in `src/sim/StepInput.ts`), so
 * a fourth box would be a fourth button and a new replay format.
 */
export const BOXES = 3;

/**
 * Where the pan is in its cycle.
 *
 *   idle      - nobody has come to the grill yet
 *   mixing    - the lasta is going; the boxes say nothing and a key buys nothing
 *   choosing  - three boxes, 0.75 s, one of them is the meat
 *   sick      - Kimmo ate a vegetable and is dealing with it
 *   done      - three on the plate; Mäysä steps aside and the pan stops
 */
export type AamupalaPhase = 'idle' | 'mixing' | 'choosing' | 'sick' | 'done';

/** Everything the renderer, the HUD overlay and the reference bot read back out. */
export interface AamupalaView {
  phase: AamupalaPhase;
  /** 0..1 through whatever phase this is. Always 0 while idle and while done. */
  progress: number;
  /** What is in each box, left to right. Meaningless outside `choosing`/`mixing`. */
  boxes: readonly Food[];
  /** Which of the three has the meat in it, 0..2. */
  meatSlot: number;
  /** How many helpings are on the plate. */
  plate: number;
  /** And which meats they were, oldest first, so the plate can be drawn. */
  served: readonly Food[];
  /** How many it needs. `AAMUPALA.servings`, carried here so the UI has one source. */
  servings: number;
}

/** What one step of the pan did. Null on the overwhelming majority of steps. */
export interface AamupalaEvents {
  /** Kimmo arrived at the grill and the first hand is being dealt. */
  started: boolean;
  /** A fresh hand went down. */
  dealt: boolean;
  /** A helping of meat landed on the plate. */
  served: boolean;
  /** A vegetable did not stay down. */
  spewed: boolean;
  /** The third helping: the doorway is clear. Fires on the single step it fills. */
  done: boolean;
}

const MIX_STEPS = Math.round(AAMUPALA.mixDuration * SIMULATION_HZ);
const VOMIT_STEPS = Math.round(AAMUPALA.vomitDuration * SIMULATION_HZ);

/**
 * The two streams a round is dealt from.
 *
 * Two rather than one because the meat is drawn by walking every round since
 * Kimmo arrived (see `meatIndex`) and the plate is drawn once, and a single
 * stream would entangle them: pulling one more number for this round's
 * vegetables would silently re-deal every meat that came before it.
 */
const STREAM = { meat: 0, plate: 1 } as const;

/**
 * Scrambles a round of one stream into a seed.
 *
 * The arrival step is the important half and it is avalanched for the same
 * reason `sipSeed` is: two runs that reached the pan a single step apart have
 * to deal completely unrelated hands, or a player who could shave 8 ms off his
 * approach could choose the order of his own boxes.
 */
function dealSeed(startStep: number, round: number, stream: number): number {
  let h =
    Math.imul(startStep | 0, 0x9e3779b1) ^
    Math.imul((round | 0) + 1, 0x85ebca6b) ^
    Math.imul((stream | 0) + 1, 0x27d4eb2f);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h ^ AAMUPALA.seed) >>> 0;
}

/**
 * Which meat is out this round, as an index into `MEATS`.
 *
 * The one rule the deal has that is not a fresh roll: never the meat that was
 * out last round. Three helpings is three rounds if they all land, and three
 * rounds of pihviä in a row would be a toll you could play by silhouette after
 * the first one - the box worth pressing has to be *found* each time.
 *
 * Which is why it is a walk from round zero rather than a lookback. Keeping
 * last round's meat on the class would make the deal a fact about the visit
 * rather than a function of two numbers, and `rollBoxes` would stop being
 * something the server could ask about a round in isolation. A visit is a
 * handful of rounds, so the walk is a handful of hashes.
 */
function meatIndex(startStep: number, round: number): number {
  let index = (mulberry32(dealSeed(startStep, 0, STREAM.meat))() * MEATS.length) | 0;
  for (let r = 1; r <= round; r++) {
    // Rolled among the *others* and stepped past the one that is sitting
    // there, rather than re-rolled until it differs: a rejection loop takes a
    // different count of numbers out of the stream depending on what it rolled,
    // and every number in here has to be spent the same way every time.
    const among = (mulberry32(dealSeed(startStep, r, STREAM.meat))() * (MEATS.length - 1)) | 0;
    index = (index + 1 + among) % MEATS.length;
  }
  return index;
}

/**
 * The hand. Pure: same arrival step and same round, same three boxes, forever.
 *
 * The *slot* is rolled first, off nothing but the seed, and that ordering is
 * load-bearing rather than tidy. Which box holds the meat is the only half of
 * a hand a run can be timed against - what is in the other two decides nothing
 * but a picture - so rolling it before either list has been looked at is what
 * lets a sixth vegetable be added without retiring the leaderboard. See the
 * hashing note in `src/config/aamupala.ts`.
 *
 * The vegetables are then drawn fresh, two different ones, and they move as
 * well as change: a round where only the meat travelled would be a round you
 * played by watching the two boxes that did not.
 */
export function rollBoxes(startStep: number, round: number): Food[] {
  const rng = mulberry32(dealSeed(startStep, round, STREAM.plate));

  const slot = (rng() * BOXES) | 0;
  const first = (rng() * VEGES.length) | 0;
  // The same step-past trick as the meats, for the same reason and to the same
  // end: two boxes of falafelia is a hand with two right answers in it.
  const second = (first + 1 + ((rng() * (VEGES.length - 1)) | 0)) % VEGES.length;
  const veges: Food[] = [VEGES[first], VEGES[second]];

  const boxes: Food[] = [];
  for (let box = 0, vege = 0; box < BOXES; box++) {
    boxes.push(box === slot ? MEATS[meatIndex(startStep, round)] : veges[vege++]);
  }
  return boxes;
}

function noEvents(): AamupalaEvents {
  return { started: false, dealt: false, served: false, spewed: false, done: false };
}

export class Aamupala {
  /** The step Kimmo arrived at the pan, which is the whole seed. Null until he does. */
  private startStep: number | null = null;
  /** The step the current hand's mix began. */
  private roundStart = 0;
  private round = 0;
  /** The step Kimmo ate a vegetable on, while he is still bringing it up. */
  private sickStep: number | null = null;

  private boxes: Food[] = rollBoxes(0, 0);
  /**
   * The meats taken so far, oldest first. This is the plate: how full it is is
   * how long it is, because a count kept beside it is a second answer to the
   * one question Mäysä asks.
   */
  private served: Food[] = [];
  private phase: AamupalaPhase = 'idle';
  private phaseStart = 0;
  /** The step `simulate` was last called with. See `progress`. */
  private step = 0;

  constructor(
    private readonly spec: AamupalaSpec,
    /** The box that is Mäysä while he is filling the porstua. */
    private readonly gate: Collider,
  ) {}

  /** True once the plate is full, which is the only thing the door cares about. */
  get isDone(): boolean {
    return this.served.length >= AAMUPALA.servings;
  }

  /** How many helpings of meat are on the plate. */
  get servings(): number {
    return this.served.length;
  }

  /** True while a key press would be worth something. */
  get canPick(): boolean {
    return this.phase === 'choosing';
  }

  /** Which box has the meat in it this round, 0..2. */
  get meatSlot(): number {
    return this.boxes.findIndex(isMeat);
  }

  view(): AamupalaView {
    return {
      phase: this.phase,
      progress: this.progress,
      boxes: this.boxes,
      meatSlot: this.meatSlot,
      plate: this.served.length,
      served: this.served,
      servings: AAMUPALA.servings,
    };
  }

  reset(): void {
    this.startStep = null;
    this.roundStart = 0;
    this.round = 0;
    this.sickStep = null;
    this.boxes = rollBoxes(0, 0);
    this.served = [];
    this.phase = 'idle';
    this.phaseStart = 0;
    this.step = 0;
    this.gate.enabled = true;
  }

  /**
   * One fixed step: the pan, the plate and the man in the doorway.
   *
   * `pick` is 0..2 for a box, or -1 for "no key this step" - the caller has
   * already turned the three edge-triggered bits into one of those, because
   * which bit means which box is a property of the input format and not of
   * breakfast.
   *
   * The cycle runs on whether Kimmo is *in reach*, and picks only count while
   * he is. Walking away does not pause it: the cook keeps working his pan, and
   * a player who leaves half-fed comes back to a hand already in progress. The
   * alternative - a machine that stops when nobody is watching it - would mean
   * the deal depended on the whole path taken between two visits rather than on
   * one step number, and the seed would stop being a seed.
   */
  simulate(feet: Vec3, step: number, pick: number): AamupalaEvents | null {
    const events = noEvents();
    this.step = step;

    if (this.isDone) {
      // Latched. He has stepped aside and there is nothing left to deal.
      this.gate.enabled = false;
      return null;
    }

    if (this.startStep === null) {
      if (!this.inReach(feet)) return null;
      this.startStep = step;
      this.beginRound(step, 0);
      events.started = true;
      events.dealt = true;
      return events;
    }

    // Coming back up. Nothing is dealt while this is running.
    if (this.sickStep !== null) {
      if (step - this.sickStep < VOMIT_STEPS) return null;
      this.sickStep = null;
      this.beginRound(step, this.round + 1);
      events.dealt = true;
      return events;
    }

    // Where the hand is, settled before anything reads it. `canPick` and the
    // window a press actually lands in have to be the same condition rather
    // than two that agree most of the time - the bot reads one and the pan
    // charges by the other.
    const elapsed = step - this.roundStart;
    if (elapsed >= MIX_STEPS + this.pickSteps) {
      // The hand ran out untouched: sweep it up and deal again.
      this.beginRound(step, this.round + 1);
      events.dealt = true;
      return events;
    }
    const phase: AamupalaPhase = elapsed < MIX_STEPS ? 'mixing' : 'choosing';
    if (phase !== this.phase) {
      this.phase = phase;
      this.phaseStart = this.roundStart + MIX_STEPS;
    }

    // A press, if there was one and if it lands inside the window.
    if (pick >= 0 && pick < BOXES && this.phase === 'choosing') {
      if (isMeat(this.boxes[pick])) {
        this.served.push(this.boxes[pick]);
        if (this.isDone) {
          // Third one. The pan stops mid-round and the doorway opens on this
          // step rather than on the next one - the plate is what Mäysä is
          // watching, not the clock.
          this.phase = 'done';
          this.phaseStart = step;
          this.gate.enabled = false;
          events.served = true;
          events.done = true;
          return events;
        }
        this.beginRound(step, this.round + 1);
        events.served = true;
        events.dealt = true;
        return events;
      }
      this.sickStep = step;
      this.phase = 'sick';
      this.phaseStart = step;
      events.spewed = true;
      return events;
    }

    return null;
  }

  private beginRound(step: number, round: number): void {
    this.round = round;
    this.roundStart = step;
    this.boxes = rollBoxes(this.startStep ?? step, round);
    this.phase = 'mixing';
    this.phaseStart = step;
  }

  /**
   * 0..1 through the current phase, off the step count alone.
   *
   * Recomputed rather than stored because `simulate` returns early on almost
   * every step, and a progress that only advanced on the steps something
   * happened would be a bar that jumped.
   */
  private get progress(): number {
    const span = this.phaseSteps;
    if (span <= 0) return 0;
    return Math.min(1, Math.max(0, (this.step - this.phaseStart) / span));
  }

  private get pickSteps(): number {
    return Math.round(
      (AAMUPALA.pickDuration - this.served.length * AAMUPALA.pickReductionPerServing) * SIMULATION_HZ,
    );
  }

  private get phaseSteps(): number {
    switch (this.phase) {
      case 'mixing':
        return MIX_STEPS;
      case 'choosing':
        return this.pickSteps;
      case 'sick':
        return VOMIT_STEPS;
      default:
        return 0;
    }
  }

  /** Horizontal only: the plate is at hip height and Kimmo is on the ground. */
  private inReach(feet: Vec3): boolean {
    const dx = feet.x - this.spec.station[0];
    const dz = feet.z - this.spec.station[1];
    return dx * dx + dz * dz <= this.spec.reach * this.spec.reach;
  }
}
